import { Head, Link, useForm } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface Message {
  id: number
  name: string
  subject: string
  body: string
}

interface Recipient {
  type: "customer" | "contact"
  id: number | string
  name: string
  first_name: string | null
  last_name: string | null
  company_name: string | null
  email: string
  title?: string | null
  primary: boolean
}

interface ResourceItem {
  id: number
  name: string
}

interface ResourceGroup {
  type_id: number
  type_name: string
  resources: ResourceItem[]
}

interface Props {
  customer: Customer
  messages: Message[]
  recipients: Recipient[]
  resources_by_type: ResourceGroup[]
  from_name: string
  from_email: string
  signature: string
}

export default function Index({
  customer,
  messages,
  recipients,
  resources_by_type,
  from_name,
  from_email,
  signature,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Email", href: `/customers/${customer.id}/emails` },
  ]

  const { data, setData, post, processing, errors, transform } = useForm({
    subject: "",
    body: "",
    include_customer: false,
    bcc_me: false,
    recipient_ids: [] as number[],
    resource_ids: [] as number[],
  })

  transform((data) => ({ email: data }))

  const [showPreview, setShowPreview] = useState(false)

  const handleTemplateSelect = (msg: Message) => {
    setData((prev) => ({
      ...prev,
      subject: msg.subject || "",
      body: msg.body || "",
    }))
  }

  const handleRecipientToggle = (recipient: Recipient) => {
    if (recipient.type === "customer") {
      setData("include_customer", !data.include_customer)
    } else {
      const id = recipient.id as number
      const current = data.recipient_ids
      if (current.includes(id)) {
        setData(
          "recipient_ids",
          current.filter((r) => r !== id),
        )
      } else {
        setData("recipient_ids", [...current, id])
      }
    }
  }

  const isRecipientSelected = (recipient: Recipient) => {
    if (recipient.type === "customer") return data.include_customer
    return data.recipient_ids.includes(recipient.id as number)
  }

  const handleResourceToggle = (resourceId: number) => {
    const current = data.resource_ids
    if (current.includes(resourceId)) {
      setData(
        "resource_ids",
        current.filter((r) => r !== resourceId),
      )
    } else {
      setData("resource_ids", [...current, resourceId])
    }
  }

  const selectedCount =
    (data.include_customer ? 1 : 0) + data.recipient_ids.length

  const selectedRecipients = recipients.filter((r) => isRecipientSelected(r))

  const replacePlaceholders = (text: string) => {
    return text
      .replace(/\[\[RecipientFirst\]\]/g, "(First Name)")
      .replace(/\[\[RecipientLast\]\]/g, "(Last Name)")
      .replace(/\[\[RecipientCompany\]\]/g, "(Company Name)")
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/customers/${customer.id}/emails`)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Email - ${customer.display_name}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Email {customer.display_name}
          </h1>
          <Link
            href={`/customers/${customer.id}`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Customer
          </Link>
        </div>

        {!showPreview ? (
          <form onSubmit={handleSubmit}>
            {/* Message Templates */}
            {messages.length > 0 && (
              <div className="border-hpi-light-gray mb-6 rounded border p-4">
                <h2 className="mb-2 text-lg font-medium">Message Templates</h2>
                <div className="flex flex-wrap gap-2">
                  {messages.map((msg) => (
                    <button
                      key={msg.id}
                      type="button"
                      onClick={() => handleTemplateSelect(msg)}
                      className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-3 py-1 text-sm"
                    >
                      {msg.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Compose */}
            <div className="border-hpi-light-gray mb-6 rounded border p-4">
              <h2 className="mb-3 text-lg font-medium">Compose Message</h2>

              <div className="mb-3">
                <label className="mb-1 block text-sm font-medium">
                  Subject
                </label>
                <input
                  type="text"
                  value={data.subject}
                  onChange={(e) => setData("subject", e.target.value)}
                  maxLength={100}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  placeholder="Email subject..."
                />
                {errors.subject && (
                  <p className="text-hpi-red mt-1 text-sm">{errors.subject}</p>
                )}
              </div>

              <div className="mb-3">
                <label className="mb-1 block text-sm font-medium">Body</label>
                <textarea
                  value={data.body}
                  onChange={(e) => setData("body", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  rows={10}
                  placeholder="Email body..."
                />
                {errors.body && (
                  <p className="text-hpi-red mt-1 text-sm">{errors.body}</p>
                )}
              </div>

              <div className="bg-hpi-pale-gray rounded p-3 text-sm">
                <p className="mb-1 font-medium">Available Placeholders:</p>
                <code className="text-hpi-dark-blue">[[RecipientFirst]]</code> -
                Recipient&apos;s first name
                <br />
                <code className="text-hpi-dark-blue">[[RecipientLast]]</code> -
                Recipient&apos;s last name
                <br />
                <code className="text-hpi-dark-blue">
                  [[RecipientCompany]]
                </code>{" "}
                - Recipient&apos;s company name
                <p className="text-hpi-red mt-2 text-xs">
                  Placeholder replacement is exact match &mdash; brackets &amp;
                  capitalization must match exactly. Placeholders will be
                  replaced in Subject &amp; Body.
                </p>
              </div>

              {/* Signature Preview */}
              {signature && (
                <div className="mt-4">
                  <label className="mb-1 block text-sm font-medium">
                    Signature Preview
                  </label>
                  <div
                    className="border-hpi-light-gray bg-hpi-pale-gray rounded border p-3 text-sm"
                    dangerouslySetInnerHTML={{ __html: signature }}
                  />
                </div>
              )}
            </div>

            {/* Select Attachments */}
            {resources_by_type.length > 0 && (
              <div className="border-hpi-light-gray mb-6 rounded border p-4">
                <h2 className="mb-3 text-lg font-medium">Select Attachments</h2>
                <p className="mb-3 text-sm">
                  Select resources to combine for brochure:
                </p>
                <div className="space-y-3">
                  {resources_by_type.map((group) => (
                    <div key={group.type_id}>
                      <div className="mb-1 flex items-center gap-2 text-sm font-medium">
                        <span className="text-amber-500">&#128193;</span>
                        {group.type_name}
                      </div>
                      <ul className="ml-6 space-y-1">
                        {group.resources.map((resource) => (
                          <li key={resource.id}>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={data.resource_ids.includes(
                                  resource.id,
                                )}
                                onChange={() =>
                                  handleResourceToggle(resource.id)
                                }
                              />
                              {resource.name}
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                {data.resource_ids.length > 0 && (
                  <p className="text-hpi-dark-blue mt-3 text-sm">
                    {data.resource_ids.length} document
                    {data.resource_ids.length !== 1 ? "s" : ""} selected for
                    brochure attachment
                  </p>
                )}
              </div>
            )}

            {/* Recipients */}
            <div className="border-hpi-light-gray mb-6 rounded border p-4">
              <h2 className="mb-3 text-lg font-medium">Recipients</h2>

              <div className="mb-2 text-sm">
                <span className="font-medium">From:</span> {from_name} &lt;
                {from_email}&gt;
              </div>

              {recipients.length > 0 ? (
                <table className="border-hpi-light-gray w-full border-collapse border">
                  <thead>
                    <tr className="bg-hpi-pale-gray">
                      <th className="border-hpi-light-gray w-10 border px-3 py-2"></th>
                      <th className="border-hpi-light-gray border px-3 py-2 text-left">
                        Name
                      </th>
                      <th className="border-hpi-light-gray border px-3 py-2 text-left">
                        Title
                      </th>
                      <th className="border-hpi-light-gray border px-3 py-2 text-left">
                        Email
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((recipient) => (
                      <tr
                        key={`${recipient.type}-${recipient.id}`}
                        className={`hover:bg-hpi-pale-blue ${recipient.primary ? "font-medium" : ""}`}
                      >
                        <td className="border-hpi-light-gray border px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={isRecipientSelected(recipient)}
                            onChange={() => handleRecipientToggle(recipient)}
                          />
                        </td>
                        <td className="border-hpi-light-gray border px-3 py-2">
                          {recipient.name}
                          {recipient.primary && (
                            <span className="text-hpi-dark-blue ml-2 text-xs">
                              Primary
                            </span>
                          )}
                        </td>
                        <td className="border-hpi-light-gray border px-3 py-2">
                          {recipient.title}
                        </td>
                        <td className="border-hpi-light-gray border px-3 py-2">
                          {recipient.email}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-hpi-red text-sm">
                  No email addresses found for this customer or their contacts.
                </p>
              )}

              <div className="mt-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={data.bcc_me}
                    onChange={(e) => setData("bcc_me", e.target.checked)}
                  />
                  BCC me on this email
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                disabled={!data.subject || !data.body || selectedCount === 0}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
              >
                Preview
              </button>
              <span className="text-hpi-med-gray text-sm">
                {selectedCount} recipient{selectedCount !== 1 ? "s" : ""}{" "}
                selected
              </span>
            </div>
          </form>
        ) : (
          /* Preview Mode */
          <div>
            {/* Email Details */}
            <div className="border-hpi-light-gray mb-6 rounded border p-4">
              <h2 className="mb-3 text-lg font-medium">Email Details</h2>

              <div className="mb-2 text-sm">
                <span className="font-medium">From:</span> {from_name} &lt;
                {from_email}&gt;
              </div>

              <div className="mb-2 text-sm">
                <span className="font-medium">To:</span>
                <ul className="mt-1 ml-4 list-disc">
                  {selectedRecipients.map((r) => (
                    <li
                      key={`${r.type}-${r.id}`}
                      className={
                        r.primary ? "text-hpi-dark-blue font-medium" : ""
                      }
                    >
                      {r.name}
                      {r.title ? ` / ${r.title}` : ""} ({r.email})
                      {r.primary && (
                        <span className="text-hpi-dark-blue ml-1 text-xs">
                          Primary
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {data.bcc_me && (
                <div className="mb-2 text-sm">
                  <span className="font-medium">BCC:</span> {from_name} &lt;
                  {from_email}&gt;
                </div>
              )}
            </div>

            {/* Message Preview */}
            <div className="border-hpi-light-gray mb-6 rounded border p-4">
              <h2 className="mb-3 text-lg font-medium">Message Preview</h2>

              <div className="mb-3">
                <span className="text-sm font-medium">Subject:</span>
                <p className="mt-1">{replacePlaceholders(data.subject)}</p>
              </div>

              <div className="mb-3">
                <span className="text-sm font-medium">Body:</span>
                <div className="bg-hpi-pale-gray mt-1 rounded p-3 whitespace-pre-wrap">
                  {replacePlaceholders(data.body)}
                </div>
              </div>

              {signature && (
                <div>
                  <span className="text-sm font-medium">Signature:</span>
                  <div
                    className="bg-hpi-pale-gray mt-1 rounded p-3"
                    dangerouslySetInnerHTML={{ __html: signature }}
                  />
                </div>
              )}

              {data.resource_ids.length > 0 && (
                <div className="mt-3">
                  <span className="text-sm font-medium">
                    Attachments ({data.resource_ids.length}):
                  </span>
                  <ul className="mt-1 list-disc pl-5 text-sm">
                    {resources_by_type
                      .flatMap((g) => g.resources)
                      .filter((r) => data.resource_ids.includes(r.id))
                      .map((r) => (
                        <li key={r.id}>{r.name}</li>
                      ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="border-hpi-light-gray rounded border px-4 py-2"
              >
                Back to Edit
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={processing}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
              >
                {processing ? "Sending..." : "Send Email"}
              </button>
              <span className="text-hpi-med-gray text-sm">
                Sending to {selectedCount} recipient
                {selectedCount !== 1 ? "s" : ""}
                {data.bcc_me ? " (+ BCC to you)" : ""}
              </span>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
